import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const getOrCreateVisitorId = (): string => {
  const key = "visitor_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

const sendHeartbeat = (visitorId: string) => {
  supabase
    .from("active_visitors")
    .upsert({ visitor_id: visitorId, last_seen: new Date().toISOString() }, { onConflict: "visitor_id" })
    .then(({ error }) => {
      if (error) console.error("Heartbeat error:", error);
    });
};

/** Returns true if the current user is a logged-in admin. */
const checkIsAdmin = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return false;
    const { data } = await supabase.rpc("has_role", {
      _user_id: session.user.id,
      _role: "admin",
    });
    return data === true;
  } catch {
    return false;
  }
};

const isLovablePreview = (): boolean => {
  const host = window.location.hostname;
  return host.includes("lovableproject.com") || host.includes("lovable.dev");
};

export const usePageTracking = () => {
  const location = useLocation();

  // Track page visit
  useEffect(() => {
    if (isLovablePreview()) return; // Never track from Lovable preview

    const visitorId = getOrCreateVisitorId();

    checkIsAdmin().then((isAdmin) => {
      if (isAdmin) return; // Don't track admins
      supabase
        .from("page_visits")
        .insert({
          visitor_id: visitorId,
          path: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent || null,
        })
        .then(({ error }) => {
          if (error) console.error("Page tracking error:", error);
        });
    });
  }, [location.pathname]);

  // Heartbeat for active visitors — skip for admins and Lovable preview
  useEffect(() => {
    if (isLovablePreview()) return;

    const visitorId = getOrCreateVisitorId();
    let interval: ReturnType<typeof setInterval> | null = null;

    checkIsAdmin().then((isAdmin) => {
      if (isAdmin) return;
      sendHeartbeat(visitorId);
      interval = setInterval(() => sendHeartbeat(visitorId), 30_000);
    });

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);
};
